import { ApiError, UnauthorizedError, type ApiErrorCode, type FieldError } from './errors';

/**
 * The single fetch seam for the SPA (ADR 0002).
 *
 * Responsibilities:
 *  1. CSRF — lazily GET /auth/csrf once, cache the token in memory, and echo it
 *     as `x-csrf-token` on every unsafe verb (POST/PATCH/DELETE). The cache is
 *     INVALIDATED on login and logout because the server rotates the token; the
 *     next unsafe call re-fetches (prevents a spurious 403 from a stale token).
 *  2. 401 — throws `UnauthorizedError` so the router/auth context can redirect.
 *  3. Error envelope — non-2xx bodies parse into a typed `ApiError`; malformed or
 *     empty bodies synthesize an envelope so callers get a stable shape.
 *  4. Observability — every error carries `requestId` (body or `x-request-id`).
 *
 * `credentials: 'include'` is set on every request so cookies flow.
 */

const UNSAFE_METHODS = new Set(['POST', 'PATCH', 'DELETE', 'PUT']);

const ERROR_CODES: ReadonlySet<string> = new Set<ApiErrorCode>([
  'unauthorized',
  'too_many_requests',
  'bad_request',
  'forbidden',
  'not_found',
  'conflict',
  'unprocessable_entity',
  'internal_error',
  'network_error',
]);

interface RawEnvelope {
  error?: {
    code?: unknown;
    message?: unknown;
    requestId?: unknown;
    fields?: unknown;
  };
}

/** A member's org, as returned by GET/POST /orgs. `role` is null until resolved. */
export interface OrgSummary {
  id: string;
  name: string;
  role: 'org_admin' | 'editor' | 'reviewer' | 'viewer' | null;
  createdAt: string;
}

/** The crawler's robots.txt stance. `honor` obeys disallow rules; `override`
 * (verified owner only) ignores robots.txt for the domain. */
export type RobotsPolicy = 'honor' | 'override';

/** A project, as returned by the projects routes. */
export interface ProjectSummary {
  id: string;
  name: string;
  authorizedHost: string;
  webPropertyUrl: string;
  domainVerificationStatus: string;
  robotsPolicy: RobotsPolicy;
  crawlPolicy: unknown;
  createdAt: string;
  hasCompletedScan: boolean;
  journeyCount: number;
  testCaseCount: number;
}

export interface ProjectInput {
  name: string;
  webPropertyUrl: string;
  authorizedHost: string;
  /** Only sent on edit (PATCH) — override requires a verified domain. */
  robotsPolicy?: RobotsPolicy;
}

/** A domain-verification challenge status. */
export type ChallengeStatus = 'pending' | 'verified' | 'expired' | 'superseded';

/** A challenge probe method. */
export type ProbeMethod = 'dns_txt' | 'https_file';

/**
 * Result of issuing (or rotating) a challenge — POST …/domain-verification (201).
 * The plaintext `token` is returned ONCE here and never again (the server only
 * persists its hash), so the UI must surface it immediately.
 */
export interface IssueChallengeResult {
  challengeId: string;
  status: 'pending';
  token: string;
  expiresAt: string;
  pollIntervalSeconds: number;
  maxWaitSeconds: number;
  dnsTxt: { recordName: string; value: string };
  httpsFile: { url: string; content: string };
}

/** Result of a manual re-check — POST …/domain-verification/check (200). */
export interface CheckChallengeResult {
  status: 'pending' | 'verified' | 'expired';
  lastCheckedAt: string | null;
  failureReason: string | null;
}

/**
 * Current verification state — GET …/domain-verification (200). Never returns the
 * token value (the server holds only the hash); `challenge` is null when none has
 * been issued.
 */
export interface DomainVerificationStatusResult {
  domainVerificationStatus: 'unverified' | 'pending' | 'verified';
  challenge: null | {
    status: ChallengeStatus;
    lastMethod: ProbeMethod | null;
    failureReason: string | null;
    issuedAt: string;
    expiresAt: string;
    lastCheckedAt: string | null;
    dnsTxt: { recordName: string };
    httpsFile: { url: string };
  };
}

/** Org data-retention settings, as returned by the retention routes. */
export interface RetentionSummary {
  orgId: string;
  evidenceDays: number;
  executionHistoryMonths: number;
  journeyHealthWindowDays: number;
  updatedAt: string;
}

export interface RetentionInput {
  evidenceDays?: number;
  executionHistoryMonths?: number;
  journeyHealthWindowDays?: number;
}

/** The BYOK LLM providers the picker supports (Story #32 stores; #31 validates). */
export type LlmProvider = 'openai' | 'anthropic' | 'deepseek' | 'custom';

/**
 * A supported provider's metadata from GET /orgs/:orgId/llm-key/providers
 * (Story #31a / FR-11.4). The registry is the single source of truth for the
 * provider list, key-page links, default models, and base URL — the client never
 * hardcodes them. `baseUrl` is `null` and `requiresBaseUrl` is true only for
 * `custom`, where the customer supplies (and the server SSRF-validates) an endpoint.
 */
export interface LlmProviderDescriptor {
  id: LlmProvider;
  label: string;
  keyPageUrl: string;
  defaultModels: string[];
  baseUrl: string | null;
  requiresBaseUrl: boolean;
}

/** Whether the stored key passed the provider handshake (Story #31a / FR-11.5). */
export type LlmValidationStatus = 'pending' | 'validated' | 'failed';

/**
 * Input for the non-persisting verify+list step (Story #196 1b). Mirrors
 * POST /orgs/:orgId/llm-key/models. `workspaceId` is Anthropic-only (org-level
 * keys require it; workspace-scoped keys omit it). Nothing is persisted.
 */
export interface LlmModelsInput {
  provider: LlmProvider;
  apiKey: string;
  baseUrl?: string;
  workspaceId?: string;
}

/** The models a proven key can use (server-narrowed). */
export interface LlmModelsResult {
  models: string[];
}

/**
 * The masked LLM-key status from GET/PUT /orgs/:orgId/llm-key. Deliberately has
 * NO plaintext field — a key value can never reach the client. `configured:false`
 * distinguishes "never set" from an active/revoked key.
 */
export type LlmKeyStatus =
  | { configured: false }
  | {
      configured: true;
      provider: LlmProvider;
      last4: string;
      status: 'active' | 'revoked';
      model: string | null;
      validationStatus: LlmValidationStatus;
      validatedAt: string | null;
      validationReason: string | null;
      createdAt: string;
      rotatedAt: string | null;
      revokedAt: string | null;
    };

// --- BYOK capability probe (Story 31-B, FR-11.6) ---

export type CapabilityStatus = 'verified' | 'limited' | 'inconclusive';
export type CapabilityRunState = 'queued' | 'running' | 'done' | 'failed';
export type CapabilityReason =
  | 'meets_floor'
  | 'below_floor'
  | 'context_below_minimum'
  | 'context_unknown'
  | 'insufficient_sample'
  | 'scorer_error'
  | 'incomplete_run'
  | 'inference_failed';

/** Disclosed probe cost. `estimatedMicroUsd` is an integer micro-USD decimal string. */
export interface CapabilityQuoteView {
  quoteAvailable: boolean;
  /** True only when a probe would actually spend (priced + context known ≥ floor). */
  spendRequired: boolean;
  reason: 'price_unavailable' | null;
  configIdentity: string;
  evalSetVersion: string;
  evalSetChecksum: string;
  pricingVersion: string | null;
  estimatedMicroUsd: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
}

export interface CapabilityConsentView {
  consentId: string;
  expiresAt: string;
  quotedMicroUsd: string;
  pricingVersion: string;
}

/** Async probe run; poll until `state` is `done`/`failed`. Only non-secret fields. */
export interface CapabilityRunView {
  runId: string;
  state: CapabilityRunState;
  status: CapabilityStatus | null;
  reason: CapabilityReason | null;
  score: number | null;
  model: string;
  evalSetVersion: string;
  evalSetChecksum: string;
  probeVersion: string;
  createdAt: string;
  finishedAt: string | null;
  cached: boolean;
}

/** A scan of a project, as returned by GET …/scans (most-recent-first). */
export interface ScanSummary {
  id: string;
  status: 'initiated' | 'running' | 'completed' | 'failed' | 'cancelled';
  createdAt: string; // ISO from the server Date
  completedAt: string | null;
  initiatedByUserId: string | null;
  /** Future run instant for a one-off scheduled scan (#288/#332); null = run now. */
  scheduledAt: string | null;
}

/** The public subset of a discovered observation's latest classification (#19).
 * `null` on the item means "not yet classified" (pending). */
export interface ObservationClassificationView {
  journeyTypes: string[];
  boundaryLabel: string;
  isBoundary: boolean;
  confidence: number;
}

/** One discovered observation from a scan's progressive results (#19). */
export interface ScanObservation {
  id: string;
  url: string;
  depth: number;
  httpMethod: string;
  statusCode: number | null;
  observedAt: string;
  classification: ObservationClassificationView | null;
}

/** A keyset page of a scan's observations (#19). `scanStatus` lets the UI stop
 * polling only on a terminal status, never on `nextCursor === null`. */
export interface ScanObservationsPage {
  scanStatus: string;
  /** Free-text reason when `scanStatus === 'failed'` (e.g. `seed_blocked_by_bot_protection`). */
  failureReason: string | null;
  items: ScanObservation[];
  nextCursor: string | null;
}

/** Discovery-time journey status. Deliberately closed to the two DISCOVERY
 * states — execution-derived states live on the SEPARATE
 * {@link JourneyHealthEntry.executionStatus} field ({@link JourneyExecutionStatus},
 * #40b), never here. */
export type JourneyStatus = 'discovered' | 'configuration_required';

/** Execution-derived outcome of a journey type's latest terminal run (#40b).
 * Mirrors the backend `RunClassifiableOutcome` (the run-reachable ExecutionOutcome
 * set, excluding the discovery-only `discovered`). `null` until a real
 * completed/cancelled run backs the type — the no-overclaim guarantee. */
export type JourneyExecutionStatus =
  | 'validated'
  | 'partially_validated'
  | 'failed'
  | 'flaky'
  | 'blocked'
  | 'error'
  | 'cancelled'
  | 'configuration_required';

/** Transaction-boundary taxonomy (mirrors the safety action classes). */
export type BoundaryKind =
  'read-only' | 'reversible' | 'state-changing' | 'financial' | 'uncertain';

/** One journey on a project (per `(journeyType, source)`, #134), with its
 * discovery-time status and (when a real terminal run backs it) the
 * execution-derived outcome of its latest run. `executionStatus`/`latestRunAt`
 * are `null` until a completed/cancelled run exists in-window. */
export interface JourneyHealthEntry {
  journeyType: string;
  /** `discovered` (scan-derived) or `customer-provided` (attested, no scan). The
   * same type can appear once per source. */
  source: JourneySource;
  /** UUID of the latest-materialized journey row for this `(type, source)`, or
   * `null` when a discovered type is classification-only. Anchors the journey-detail
   * route (#228); `null` entries have no detail link. */
  journeyId: string | null;
  status: JourneyStatus;
  /** Latest completed-scan time; `null` for a scanless customer-provided journey. */
  latestScanAt: string | null;
  boundaryKind: BoundaryKind | null;
  /** Business-impact level driving the attention ranking (#42); `null` when a
   * discovered type is classification-only (no materialized journey). */
  criticality: JourneyCriticality | null;
  executionStatus: JourneyExecutionStatus | null;
  latestRunAt: string | null;
}

/** A project's discovery-derived journey health — GET …/journey-health (200). */
export interface JourneyHealthResult {
  journeys: JourneyHealthEntry[];
  blockedCount: number;
  windowDays: number;
  hasCompletedScanInWindow: boolean;
}

/** Discovery-evidence retention state for one journey type (Story #41a). `ok`
 * has ≥1 in-window row; `expired` means every row aged out of the retention
 * window; `no-data` means nothing was ever classified. Deliberately closed —
 * execution/run/validated outcomes are structurally unrepresentable. */
export type JourneyEvidenceState = 'ok' | 'expired' | 'no-data';

/** Form-field METADATA only (S4) — never a captured value/placeholder. */
export interface FormFieldMeta {
  name: string | null;
  type: string | null;
  autocomplete: string | null;
}

/** A discovered form as method + field metadata. The action URL is dropped. */
export interface FormMeta {
  method: string;
  fields: FormFieldMeta[];
}

/** The classifier's non-authoritative reasoning behind an evidence row. */
export interface JourneyEvidenceReasoning {
  boundaryKind: BoundaryKind | null;
  confidence: number;
  matchedRules: string[];
  rulesetVersion: string;
}

/**
 * One evidence row. Discriminated on `expired`: an in-window row carries the full
 * (metadata-only) evidence; an expired row carries ONLY its id and expiry day.
 */
export type JourneyEvidenceRow =
  | {
      expired: false;
      observationId: string;
      url: string;
      depth: number;
      httpMethod: string;
      statusCode: number | null;
      forms: FormMeta[];
      linkCount: number;
      buttonCount: number;
      buttonNames: string[];
      redirectChain: string[];
      robotsExcluded: boolean;
      outOfScope: boolean;
      scanCompletedAt: string;
      reasoning: JourneyEvidenceReasoning;
    }
  | { expired: true; observationId: string; expiredAsOf: string };

/** Paginated discovery evidence for one journey type — GET …/journeys/:type/evidence (200). */
export interface JourneyEvidenceResponse {
  journeyType: string;
  windowDays: number;
  state: JourneyEvidenceState;
  observations: JourneyEvidenceRow[];
  total: number;
  returned: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

/** One detected step of a journey's ordered flow (Story #129). `orderingInferred`
 * and `tentative` carry the no-overclaim honesty markers. Mirrors the backend
 * `DetectedStepDto`; web cannot import from `app/`. */
export interface DetectedStep {
  stepOrder: number;
  actionKind: string;
  description: string;
  targetPath: string | null;
  orderingInferred: boolean;
  tentative: boolean;
  confidence: number;
}

export interface DetectedStepsResponse {
  journeyType: string;
  steps: DetectedStep[];
}

// ---- AI model revisions (#228 / Story 34) --------------------------------
// Read-only mirrors of the backend `journey-model.contract`; web cannot import
// from `app/`. HONEST-BY-DEFAULT: `verification` has the single inhabitant
// `'unverified'` and the view carries 34-C markers (`tentative`,
// `appliedThreshold`, `failClosed`) — nothing here can represent "verified".

/** One revision in the list — metadata only, no steps. */
export interface ModelRevisionSummary {
  revisionId: string;
  revisionNumber: number;
  generatorVersion: string;
  model: string;
  stepCount: number;
  createdAt: string;
  /** Exact integer micro-USD decimal string, or `null` when the cost is unknowable
   * (provider omitted usage / unpriced model / legacy row). `null` renders as
   * "unavailable" — NEVER $0 (#34-D3). */
  actualMicroUsd: string | null;
}

/** Closed set of modeled action kinds. */
export type ModeledActionKind = 'navigate' | 'interact' | 'input' | 'assert';

/** Discriminated provenance: an `observed` step is matched to real evidence; an
 * `inferred` step carries none. */
export type StepProvenance =
  | { kind: 'observed'; observationId: string; detectedStepOrder: number | null }
  | { kind: 'inferred' };

export interface ModeledStep {
  stepOrder: number;
  actionKind: ModeledActionKind;
  intent: string;
  targetHint: string | null;
  provenance: StepProvenance;
}

/** A modeled step marked by 34-C. `tentative` is rendered even when `false`
 * — `false` NEVER implies "verified". */
export interface MarkedModeledStep extends ModeledStep {
  tentative: boolean;
}

export interface DataRequirement {
  name: string;
  kind: string;
}

/** `verification` is the literal `'unverified'` — the only inhabitant. */
export interface ExpectedResult {
  stepOrder: number;
  description: string;
  verification: 'unverified';
  evidenceObservationIds: string[];
}

export interface MarkedExpectedResult extends ExpectedResult {
  tentative: boolean;
}

/** The only revision shape a consumer receives (34-C). `appliedThreshold` is the
 * finite threshold used, or `null` when `failClosed` is true. */
export interface MarkedRevisionView {
  revisionId: string;
  revisionNumber: number;
  model: string;
  createdAt: string;
  generatorVersion: string;
  preconditions: string[];
  dataRequirements: DataRequirement[];
  steps: MarkedModeledStep[];
  expectedResults: MarkedExpectedResult[];
  appliedThreshold: number | null;
  failClosed: boolean;
  /** Actual usage + computed cost (#34-D3). All nullable — absent usage, an
   * unpriced model, or a legacy revision surfaces "unavailable", never $0. */
  actualMicroUsd: string | null;
  promptTokens: number | null;
  completionTokens: number | null;
  totalTokens: number | null;
  pricingVersion: string | null;
}

/** Outcome of a modeling run (#229 / Story 34-D2). Discriminated so a caller never
 * sees a half-state. `reused:true` ⇒ an idempotency-hash match ⇒ ZERO spend (no
 * provider call). Carries NO token usage / cost — modeling-run cost is 34-D3. */
export type JourneyModelingOutcome =
  | { status: 'generated'; revisionId: string; revisionNumber: number; reused: boolean }
  | { status: 'failed'; reason: string };

/** Whether a journey is "pending AI enrichment" after a key/quota modeling failure
 * (#33, FR-11.9). Mirrors the backend `ModelingStatusView`. */
export interface JourneyModelingStatusView {
  pending: boolean;
  state: 'pending_key' | 'pending_quota' | null;
  reason: string | null;
}

/** Execution-evidence drill-down state (Story #127). `ok` = a terminal run
 * exists; `in-progress` = latest run is non-terminal; `no-data` = no run (or an
 * unknown journey type). Mirrors the backend `ExecutionEvidenceState`; web cannot
 * import from `app/`. */
export type ExecutionEvidenceState = 'ok' | 'in-progress' | 'no-data';

/** Raw run-level summary. RAW taxonomy values only — the honesty label is derived
 * client-side from (outcome, reasonCode); no user copy is baked server-side. */
export interface ExecutionRunSummary {
  runId: string;
  outcome: string | null;
  reasonCode: string | null;
  status: 'completed' | 'cancelled';
  startedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
}

/** One captured console entry (already redacted server-side). */
export interface ConsoleLogView {
  level: string;
  message: string;
}

/** One captured network error (already redacted server-side). */
export interface NetworkErrorView {
  url: string;
  method: string;
  status: number | null;
  detail: string;
}

/** Redaction audit surface — proves masking ran. The internal decisions/allow/deny
 * arrays are NOT sent by the server. */
export interface RedactionMetaView {
  strategyVersion: string;
  overRedacted: boolean;
  score: number;
  fieldsMasked: number;
  tokensStripped: number;
}

/** Per-step evidence, discriminated on `kind`. `full` carries a decoded screenshot
 * data URL; `screenshot-absent` has none (null bytea or decode failure) but keeps
 * text/logs; `expired` is retention-masked; `none` means the step has no evidence
 * row. Mirrors the backend `StepEvidence` exactly. */
export type StepEvidence =
  | {
      kind: 'full';
      screenshotDataUrl: string;
      screenshotText: string | null;
      consoleLogs: ConsoleLogView[];
      networkErrors: NetworkErrorView[];
      pageUrl: string | null;
      redactionMeta: RedactionMetaView;
      capturedAt: string;
    }
  | {
      kind: 'screenshot-absent';
      screenshotText: string | null;
      consoleLogs: ConsoleLogView[];
      networkErrors: NetworkErrorView[];
      pageUrl: string | null;
      redactionMeta: RedactionMetaView;
      capturedAt: string;
    }
  | { kind: 'expired'; expiredAsOf: string }
  | { kind: 'none' };

/** One row in the execution step grid. RAW `outcome`/`reasonCode`; the label is
 * derived client-side. Mirrors the backend `ExecutionStepRow`. */
export interface ExecutionStepRow {
  stepResultId: string;
  stepOrder: number;
  actionKind: string;
  targetPath: string | null;
  classification: string;
  phase: 'forward' | 'cleanup';
  executed: boolean;
  outcome: string;
  reasonCode: string;
  httpStatus: number | null;
  errorCount: number;
  evidence: StepEvidence;
}

/** Journey provenance scope for an execution-evidence drill-in (#250). `null` in a
 * response means the legacy all-sources request (no `source` supplied). */
export type ExecutionEvidenceSource = 'discovered' | 'customer-provided';

/** Execution-evidence bundle for a journey type's latest terminal run —
 * GET …/journeys/:type/execution-evidence (200). Discriminated on `state`.
 * Mirrors the backend `ExecutionEvidenceResponse`; web cannot import from `app/`. */
export type ExecutionEvidenceResponse =
  | {
      state: 'ok';
      journeyType: string;
      source: ExecutionEvidenceSource | null;
      windowDays: number;
      run: ExecutionRunSummary;
      steps: ExecutionStepRow[];
    }
  | {
      state: 'in-progress';
      journeyType: string;
      source: ExecutionEvidenceSource | null;
      runId: string;
      status: 'initiated' | 'running';
    }
  | { state: 'no-data'; journeyType: string; source: ExecutionEvidenceSource | null };

/** A recorded potential product defect (a run that failed a storefront
 * assertion), Story #37/37b. Mirrors the backend `DefectSummary`; web cannot
 * import from `app/`. Server sends RAW `severity`/`reviewState`/`reasonCode` — the
 * UI derives display labels client-side (honesty pattern). Scalars only. */
export interface DefectSummary {
  id: string;
  journeyType: string;
  journeyTitle: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  reviewState: string;
  reasonCode: string;
  stepOrder: number;
  occurrenceCount: number;
  firstDetectedAt: string; // ISO
  lastDetectedAt: string; // ISO
}

/** Whole-project severity counts — the dashboard header rollup. */
export interface SeverityRollup {
  critical: number;
  high: number;
  medium: number;
  low: number;
}

/** Response of GET /orgs/:orgId/projects/:id/defects (worst-first). Mirrors the
 * backend `DefectsListResponse`. */
export interface DefectsListResponse {
  defects: DefectSummary[];
  total: number;
  returned: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  rollup: SeverityRollup;
}

export interface DefectsListParams {
  limit?: number;
  offset?: number;
}

/** The six human-settable review states (`new` is system-only). Mirrors the
 * backend `HUMAN_REVIEW_STATES`; web cannot import from `app/`. */
export type HumanReviewState =
  | 'needs_investigation'
  | 'confirmed'
  | 'false_positive'
  | 'duplicate'
  | 'resolved'
  | 'ignored';

/** Every state a defect row can currently hold, including the system-only `new`.
 * Mirrors the backend `ReviewState`; used for the optimistic `expectedState`
 * guard (the caller's CURRENT state, which may be `new`). */
export type ReviewState = HumanReviewState | 'new';

/** Single-defect detail — GET …/defects/:defectId (Story #38, slice 38b). A
 * `DefectSummary` superset adding the honest review facts. Mirrors the backend
 * `DefectDetail`; web cannot import from `app/`. Scalars only. */
export interface DefectDetail extends DefectSummary {
  isPotential: boolean;
  canonicalDefectId: string | null;
}

/** Body for POST …/defects/:defectId/reviews. Mirrors the backend
 * `ReviewDefectDto`; web cannot import from `app/`. */
export interface ReviewDefectRequest {
  toState: HumanReviewState;
  note?: string;
  canonicalDefectId?: string;
  expectedState?: ReviewState;
}

/** 201 result of a successful review. Mirrors the backend `DefectReviewResult`. */
export interface DefectReviewResult {
  id: string;
  reviewState: HumanReviewState;
  isPotential: boolean;
  canonicalDefectId: string | null;
  updatedAt: string; // ISO
}

/** One recorded review transition — GET …/defects/:defectId/reviews (Story #39).
 * Mirrors the backend `DefectReviewHistoryEntry`; web cannot import from `app/`.
 * Attributed by actor TYPE only — the reviewer's identity is not exposed (D4). */
export interface DefectReviewHistoryEntry {
  id: string;
  actorType: 'user' | 'system';
  fromState: string;
  toState: string;
  note: string | null;
  createdAt: string; // ISO
}

/** A defect's review history (newest-first). Mirrors the backend
 * `DefectReviewHistoryResponse`. A never-reviewed defect returns an empty list. */
export interface DefectReviewHistoryResponse {
  defectId: string;
  reviews: DefectReviewHistoryEntry[];
}

/**
 * One deterministically generated Gherkin test case (#35). Read-only, append-only:
 * a snapshot is never mutated — editing a journey's steps or bumping the generator
 * appends a NEW row. The version anchor (`generatorVersion`/`rulesetVersion`/
 * `materializerVersion`/`stepsHash`) records exactly which logic + step content
 * produced `renderedText`. Mirrors the backend `TestCaseView` (web cannot import
 * from `app/`).
 */
export type TestCaseStatus = 'active' | 'disabled';

export interface TestCaseView {
  id: string;
  journeyId: string;
  journeyType: string;
  format: string;
  generatorVersion: string;
  rulesetVersion: string | null;
  materializerVersion: string | null;
  stepsHash: string;
  renderedText: string;
  createdAt: string;
  // Authoring / versioning (#165). `rootId` is the chain id; `status` is the
  // CURRENT lifecycle status of the whole chain.
  source: string;
  rootId: string;
  revisionNumber: number;
  supersedesId: string | null;
  authoredByUserId: string | null;
  status: TestCaseStatus;
}

/** Result of generating a test case for a journey (#35): the (new or pre-existing)
 * case + whether it was appended this call. Mirrors the backend
 * `TestCaseGenerationResult`. `created:false` means an identical case already existed. */
export interface TestCaseGenerationResult {
  created: boolean;
  testCase: TestCaseView;
}

/** Closed set of test-case export formats. Mirrors the backend `ExportFormat`
 * (`EXPORT_FORMATS`); web cannot import from `app/`. Gherkin/Playwright #36,
 * Selenium #169, Cypress #170, PyTest #448a, TestNG #448b. */
export type ExportFormat = 'gherkin' | 'playwright' | 'selenium' | 'cypress' | 'pytest' | 'testng';

/** The five MVP integrations (SRS FR-10.1). Mirrors the backend `IntegrationId`;
 * web cannot import from `app/`. */
export type IntegrationId = 'jira' | 'github' | 'gitlab' | 'linear' | 'shopify';

/** Integration connection state (SRS FR-10.3). Mirrors the backend
 * `IntegrationState`; `coming-soon` is hyphenated at the API boundary. */
export type IntegrationState = 'connected' | 'disconnected' | 'coming-soon';

/** One integration row from GET /orgs/:orgId/integrations. Mirrors the backend
 * `IntegrationSummary`; kept in sync with `app/src/orgs/integrations.service.ts`. */
export interface IntegrationSummary {
  id: IntegrationId;
  name: string;
  state: IntegrationState;
  connectedAt: string | null;
}

/** A per-project issue-tracker connection (GET /orgs/:orgId/projects/:id/tracker-settings).
 * Mirrors the backend `TrackerSettingsView`. The API token is write-only and is
 * NEVER returned — `connected` reports only whether one is stored. */
export interface TrackerSettingsView {
  provider: string;
  enabled: boolean;
  baseUrl: string | null;
  email: string | null;
  projectKey: string | null;
  githubOwner: string | null;
  githubRepo: string | null;
  gitlabProject: string | null;
  linearTeam: string | null;
  connected: boolean;
  updatedAt: string | null;
}

/** PATCH body for a tracker connection. Mirrors the backend `UpdateTrackerSettingsDto`.
 * `apiToken` is write-only (omit to keep the stored token). `clearConnection`
 * disconnects and is mutually exclusive with the connection fields. */
export interface UpdateTrackerSettingsInput {
  provider?: string;
  baseUrl?: string;
  email?: string;
  apiToken?: string;
  projectKey?: string;
  githubOwner?: string;
  githubRepo?: string;
  gitlabProject?: string;
  linearTeam?: string;
  enabled?: boolean;
  clearConnection?: boolean;
}

/**
 * Masked view of a project's Connected Validation test credentials
 * (GET/PUT …/connected-validation/credentials). Mirrors the backend
 * `TestCredentialsView`. Deliberately has NO plaintext fields — the email/password
 * pair is write-only and can never reach the client. `configured` distinguishes
 * "never saved" from a stored pair; `emailMasked` is a server-derived display mask.
 */
export interface TestCredentialsView {
  configured: boolean;
  emailMasked: string | null;
  updatedAt: string | null;
}

/** PUT body for a project's Connected Validation test credentials. Both fields are
 * write-only (sent once over HTTPS, encrypted at rest, NEVER read back). */
export interface SetTestCredentialsInput {
  email: string;
  password: string;
}

/**
 * Read-only mirror of a project's Connected Validation authorization state
 * (GET …/connected-validation). `enabled` is the boundary opt-in; `authorizedActions`
 * lists the allowlisted boundary kinds. Used to render the tri-state "Authorized"
 * row alongside the credentials status. Mirrors the backend
 * `ConnectedValidationSummary` (scalars only; web cannot import from `app/`).
 */
export interface ConnectedValidationSettingsView {
  projectId: string;
  enabled: boolean;
  authorizedActions: string[];
  updatedAt: string | null;
}

/** The four org membership roles; exactly one applies per user per org. */
export type MemberRole = 'org_admin' | 'editor' | 'reviewer' | 'viewer';

/**
 * A member, as returned by the members routes. `email` is present ONLY when the
 * caller may manage members (org_admin); non-admins receive rows without it.
 */
export interface MemberSummary {
  id: string;
  userId: string;
  role: MemberRole;
  createdAt: string;
  email?: string;
}

/** Where a journey came from — drives the source badge (#243). */
export type JourneySource = 'customer-provided' | 'discovered';

/** Customer-attested / server-derived business criticality (#243). */
export type JourneyCriticality = 'high' | 'medium' | 'low';

/** One journey in a project's list — the current snapshot per (journeyType,
 * source). Mirrors the backend JourneyListItem (#243). */
export interface JourneyListItem {
  id: string;
  journeyType: string;
  title: string;
  source: JourneySource;
  criticality: JourneyCriticality;
  isBoundary: boolean;
  actionRisk: BoundaryKind;
  stepCount: number;
  materializedAt: string;
}

/** A project's current journeys — GET …/journeys (200). */
export interface JourneyListResponse {
  journeys: JourneyListItem[];
}

/** One ordered step of a customer-provided journey (#243). `targetPath` is the
 * canonical on-host URL (or null). Rendered as TEXT, never a live href. */
export interface JourneyStepView {
  stepOrder: number;
  actionKind: string;
  description: string;
  targetPath: string | null;
}

/** A single journey plus its ordered steps — GET …/journeys/:journeyId (200). */
export interface JourneyDetailResponse extends JourneyListItem {
  steps: JourneyStepView[];
}

/** One step to ingest (mirrors the backend IngestStepDto, #21). */
export interface IngestJourneyStepInput {
  stepOrder: number;
  actionKind: string;
  description: string;
  targetPath?: string;
}

/** One journey to ingest (mirrors IngestJourneyDto, #21). `criticality` is
 * honored server-side (#243); boundary/source stay server-authoritative. */
export interface IngestJourneyInput {
  journeyType: string;
  title: string;
  criticality: JourneyCriticality;
  steps?: IngestJourneyStepInput[];
}

/** Batch ingest body — POST …/journeys (mirrors IngestJourneysDto, #21). */
export interface IngestJourneysInput {
  journeys: IngestJourneyInput[];
}

/** Result of an ingest — the created journey ids in request order. */
export interface IngestJourneysResult {
  ids: string[];
}

/** How a journey run selects journeys (mirrors the backend RUN_SELECTION_MODES, #25).
 * `single`/`group` need `journeyIds`; `all-critical` runs every top-criticality
 * customer-provided journey (no ids). Only customer-provided journeys are runnable. */
export type RunSelectionMode = 'single' | 'group' | 'all-critical';

/** Trigger-a-run body — POST …/journeys/runs (mirrors TriggerJourneyRunDto, #25). */
export interface TriggerJourneyRunInput {
  selectionMode: RunSelectionMode;
  journeyIds?: string[];
  scheduledAtMs?: number;
}

/** Result of triggering a run: the created run ids, in selection order (202). */
export interface TriggerRunResult {
  runIds: string[];
}

/** Result of cancelling a run: the run's resulting status. */
export interface CancelRunResult {
  status: string;
}

export interface ApiClient {
  getCsrf(): Promise<string>;
  login(email: string, password: string): Promise<{ userId: string; email: string }>;
  register(email: string, password: string): Promise<{ userId: string }>;
  logout(): Promise<void>;
  /** Rehydrates the caller's identity from the session cookie. 401 when anonymous. */
  me(): Promise<{ userId: string; email: string }>;
  createOrg(name: string): Promise<OrgSummary>;
  listOrgs(): Promise<OrgSummary[]>;
  createProject(orgId: string, input: ProjectInput): Promise<ProjectSummary>;
  getProject(orgId: string, id: string): Promise<ProjectSummary>;
  listProjects(orgId: string): Promise<ProjectSummary[]>;
  updateProject(orgId: string, id: string, input: Partial<ProjectInput>): Promise<ProjectSummary>;
  issueDomainVerification(orgId: string, id: string): Promise<IssueChallengeResult>;
  checkDomainVerification(orgId: string, id: string): Promise<CheckChallengeResult>;
  getDomainVerification(orgId: string, id: string): Promise<DomainVerificationStatusResult>;
  getRetention(orgId: string): Promise<RetentionSummary>;
  updateRetention(orgId: string, input: RetentionInput): Promise<RetentionSummary>;
  getLlmKey(orgId: string): Promise<LlmKeyStatus>;
  getLlmProviders(orgId: string): Promise<LlmProviderDescriptor[]>;
  setLlmKey(
    orgId: string,
    input: {
      provider: LlmProvider;
      apiKey: string;
      model: string;
      baseUrl?: string;
      workspaceId?: string;
    },
  ): Promise<LlmKeyStatus>;
  listLlmKeyModels(orgId: string, input: LlmModelsInput): Promise<LlmModelsResult>;
  revokeLlmKey(orgId: string): Promise<void>;
  getCapabilityQuote(orgId: string): Promise<CapabilityQuoteView>;
  recordCapabilityConsent(orgId: string): Promise<CapabilityConsentView>;
  enqueueCapabilityProbe(orgId: string): Promise<CapabilityRunView>;
  getLatestCapabilityRun(orgId: string): Promise<CapabilityRunView | null>;
  getCapabilityRun(orgId: string, runId: string): Promise<CapabilityRunView | null>;
  getIntegrations(orgId: string): Promise<IntegrationSummary[]>;
  getTrackerSettings(orgId: string, projectId: string): Promise<TrackerSettingsView>;
  updateTrackerSettings(
    orgId: string,
    projectId: string,
    input: UpdateTrackerSettingsInput,
  ): Promise<TrackerSettingsView>;
  getConnectedValidationSettings(
    orgId: string,
    projectId: string,
  ): Promise<ConnectedValidationSettingsView>;
  getTestCredentials(orgId: string, projectId: string): Promise<TestCredentialsView>;
  setTestCredentials(
    orgId: string,
    projectId: string,
    input: SetTestCredentialsInput,
  ): Promise<TestCredentialsView>;
  deleteTestCredentials(orgId: string, projectId: string): Promise<void>;
  listScans(orgId: string, projectId: string): Promise<ScanSummary[]>;
  getScanObservations(
    orgId: string,
    projectId: string,
    scanId: string,
    opts?: { limit?: number; cursor?: string },
  ): Promise<ScanObservationsPage>;
  runScan(
    orgId: string,
    projectId: string,
    body?: { scheduledAtMs?: number },
  ): Promise<ScanSummary>;
  stopScan(orgId: string, projectId: string, scanId: string): Promise<ScanSummary>;
  journeyHealth(orgId: string, projectId: string): Promise<JourneyHealthResult>;
  journeyEvidence(
    orgId: string,
    projectId: string,
    journeyType: string,
  ): Promise<JourneyEvidenceResponse>;
  journeyDetectedSteps(
    orgId: string,
    projectId: string,
    journeyType: string,
  ): Promise<DetectedStepsResponse>;
  getJourneyModelRevisions(
    orgId: string,
    projectId: string,
    journeyId: string,
  ): Promise<ModelRevisionSummary[]>;
  getJourneyModelRevision(
    orgId: string,
    projectId: string,
    journeyId: string,
    revisionId: string,
  ): Promise<MarkedRevisionView>;
  postJourneyModel(
    orgId: string,
    projectId: string,
    journeyId: string,
  ): Promise<JourneyModelingOutcome>;
  /** Whether a journey is pending AI enrichment after a key/quota failure (#33). */
  getJourneyModelingStatus(
    orgId: string,
    projectId: string,
    journeyId: string,
  ): Promise<JourneyModelingStatusView>;
  executionEvidence(
    orgId: string,
    projectId: string,
    journeyType: string,
    source?: ExecutionEvidenceSource,
  ): Promise<ExecutionEvidenceResponse>;
  listJourneys(orgId: string, projectId: string): Promise<JourneyListResponse>;
  getJourneyDetail(
    orgId: string,
    projectId: string,
    journeyId: string,
  ): Promise<JourneyDetailResponse>;
  ingestJourneys(
    orgId: string,
    projectId: string,
    input: IngestJourneysInput,
  ): Promise<IngestJourneysResult>;
  /** Trigger a journey run (customer-provided journeys only, #25). 202 Accepted. */
  triggerJourneyRun(
    orgId: string,
    projectId: string,
    input: TriggerJourneyRunInput,
  ): Promise<TriggerRunResult>;
  /** Cancel an in-flight journey run (#25). */
  cancelJourneyRun(orgId: string, projectId: string, runId: string): Promise<CancelRunResult>;
  defectsList(
    orgId: string,
    projectId: string,
    params?: DefectsListParams,
  ): Promise<DefectsListResponse>;
  getDefect(orgId: string, projectId: string, defectId: string): Promise<DefectDetail>;
  getDefectReviewHistory(
    orgId: string,
    projectId: string,
    defectId: string,
  ): Promise<DefectReviewHistoryResponse>;
  reviewDefect(
    orgId: string,
    projectId: string,
    defectId: string,
    body: ReviewDefectRequest,
  ): Promise<DefectReviewResult>;
  /** Generate (idempotently) the deterministic Gherkin test case for a journey (#35). */
  generateTestCase(
    orgId: string,
    projectId: string,
    journeyId: string,
  ): Promise<TestCaseGenerationResult>;
  listTestCases(orgId: string, projectId: string): Promise<TestCaseView[]>;
  exportTestCase(
    orgId: string,
    projectId: string,
    testCaseId: string,
    format: ExportFormat,
  ): Promise<string>;
  /** The caller's remembered export format for a test case (defaults to gherkin). */
  getTestCaseFormatPreference(
    orgId: string,
    projectId: string,
    testCaseId: string,
  ): Promise<{ format: ExportFormat }>;
  /** Remember the caller's export format for a test case. */
  setTestCaseFormatPreference(
    orgId: string,
    projectId: string,
    testCaseId: string,
    format: ExportFormat,
  ): Promise<{ format: ExportFormat }>;
  /** Full version chain of a test case, newest revision first (#165). */
  listTestCaseVersions(
    orgId: string,
    projectId: string,
    testCaseId: string,
  ): Promise<TestCaseView[]>;
  /** Append a human-authored edit as a new version of the chain (#165). */
  editTestCase(
    orgId: string,
    projectId: string,
    testCaseId: string,
    renderedText: string,
  ): Promise<TestCaseView>;
  /** Disable or enable a test-case chain (#165). */
  setTestCaseStatus(
    orgId: string,
    projectId: string,
    testCaseId: string,
    status: TestCaseStatus,
  ): Promise<{ status: TestCaseStatus }>;
  listMembers(orgId: string): Promise<MemberSummary[]>;
  addMember(orgId: string, input: { email: string; role: MemberRole }): Promise<MemberSummary>;
  updateMemberRole(orgId: string, userId: string, role: MemberRole): Promise<MemberSummary>;
  removeMember(orgId: string, userId: string): Promise<void>;
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  del<T>(path: string): Promise<T>;
  /** Test/reset hook: clears the cached CSRF token. */
  _resetCsrf(): void;
}

export interface ApiClientOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = (options.baseUrl ?? '').replace(/\/$/, '');
  const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);

  // In-memory CSRF token cache. Deliberately not persisted — a rotated token
  // must never survive a login/logout.
  let csrfToken: string | null = null;
  let inFlightCsrf: Promise<string> | null = null;

  function url(path: string): string {
    if (/^https?:\/\//.test(path)) return path;
    return `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  }

  async function readEnvelope(res: Response): Promise<RawEnvelope | null> {
    const text = await res.text();
    if (!text) return null;
    try {
      return JSON.parse(text) as RawEnvelope;
    } catch {
      return null;
    }
  }

  function normalizeError(res: Response, raw: RawEnvelope | null): ApiError {
    const headerRequestId = res.headers.get('x-request-id') ?? undefined;
    const env = raw?.error;

    const code: ApiErrorCode = ERROR_CODES.has(env?.code as string)
      ? (env?.code as ApiErrorCode)
      : statusToCode(res.status);

    const requestId =
      (typeof env?.requestId === 'string' && env.requestId) || headerRequestId || 'unknown';

    // Never trust an empty server message; keep a stable default.
    const message =
      typeof env?.message === 'string' && env.message.length > 0
        ? env.message
        : defaultMessage(res.status);

    const fields = parseFields(env?.fields);

    if (res.status === 401) {
      return new UnauthorizedError({ message, requestId, ...(fields ? { fields } : {}) });
    }

    return new ApiError({
      code,
      message,
      requestId,
      status: res.status,
      ...(fields ? { fields } : {}),
    });
  }

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    headers: Record<string, string> = {},
  ): Promise<T> {
    const isUnsafe = UNSAFE_METHODS.has(method);
    const requestHeaders: Record<string, string> = { ...headers };

    if (isUnsafe) {
      const token = await ensureCsrf();
      requestHeaders['x-csrf-token'] = token;
    }

    let payload: string | undefined;
    if (body !== undefined) {
      requestHeaders['content-type'] = 'application/json';
      payload = JSON.stringify(body);
    }

    let res: Response;
    try {
      res = await doFetch(url(path), {
        method,
        credentials: 'include',
        headers: requestHeaders,
        ...(payload !== undefined ? { body: payload } : {}),
      });
    } catch (cause) {
      // Network failure before any response — synthesize an envelope.
      throw new ApiError({
        code: 'network_error',
        message: defaultMessage(0),
        requestId: 'unknown',
        status: 0,
      });
    }

    if (!res.ok) {
      const raw = await readEnvelope(res);
      throw normalizeError(res, raw);
    }

    if (res.status === 204) {
      return undefined as T;
    }

    const text = await res.text();
    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  }

  /** GET a plain-text body (not JSON). Shares error-envelope handling with
   * {@link request} but returns the raw text — used for test-case exports, which
   * are served as `text/plain`. */
  async function requestText(method: string, path: string): Promise<string> {
    let res: Response;
    try {
      res = await doFetch(url(path), { method, credentials: 'include' });
    } catch (cause) {
      throw new ApiError({
        code: 'network_error',
        message: defaultMessage(0),
        requestId: 'unknown',
        status: 0,
      });
    }

    if (!res.ok) {
      const raw = await readEnvelope(res);
      throw normalizeError(res, raw);
    }

    return res.text();
  }

  async function ensureCsrf(): Promise<string> {
    if (csrfToken) return csrfToken;
    if (inFlightCsrf) return inFlightCsrf;
    inFlightCsrf = fetchCsrf().finally(() => {
      inFlightCsrf = null;
    });
    return inFlightCsrf;
  }

  async function fetchCsrf(): Promise<string> {
    let res: Response;
    try {
      res = await doFetch(url('/auth/csrf'), {
        method: 'GET',
        credentials: 'include',
        headers: { accept: 'application/json' },
      });
    } catch {
      throw new ApiError({
        code: 'network_error',
        message: defaultMessage(0),
        requestId: 'unknown',
        status: 0,
      });
    }
    if (!res.ok) {
      const raw = await readEnvelope(res);
      throw normalizeError(res, raw);
    }
    const data = (await res.json()) as { csrfToken?: unknown };
    if (typeof data.csrfToken !== 'string' || data.csrfToken.length === 0) {
      throw new ApiError({
        code: 'internal_error',
        message: defaultMessage(500),
        requestId: res.headers.get('x-request-id') ?? 'unknown',
        status: 500,
      });
    }
    csrfToken = data.csrfToken;
    return csrfToken;
  }

  return {
    async getCsrf() {
      return ensureCsrf();
    },

    async login(email, password) {
      const result = await request<{ userId: string; email: string }>('POST', '/auth/login', {
        email,
        password,
      });
      // Server rotates the CSRF token on login — drop the stale one.
      csrfToken = null;
      return result;
    },

    async register(email, password) {
      // Register does NOT rotate the CSRF token (no session is established yet);
      // auto-login afterwards is what rotates it.
      return request<{ userId: string }>('POST', '/auth/register', { email, password });
    },

    async logout() {
      await request<void>('POST', '/auth/logout');
      // Server clears/rotates the CSRF token on logout — drop the stale one.
      csrfToken = null;
    },

    async me() {
      return request<{ userId: string; email: string }>('GET', '/auth/me');
    },

    createOrg(name) {
      return request<OrgSummary>('POST', '/orgs', { name });
    },

    listOrgs() {
      return request<OrgSummary[]>('GET', '/orgs');
    },

    createProject(orgId, input) {
      return request<ProjectSummary>('POST', `/orgs/${orgId}/projects`, input);
    },

    getProject(orgId, id) {
      return request<ProjectSummary>('GET', `/orgs/${orgId}/projects/${id}`);
    },

    listProjects(orgId) {
      return request<ProjectSummary[]>('GET', `/orgs/${orgId}/projects`);
    },

    updateProject(orgId, id, input) {
      return request<ProjectSummary>('PATCH', `/orgs/${orgId}/projects/${id}`, input);
    },

    issueDomainVerification(orgId, id) {
      return request<IssueChallengeResult>(
        'POST',
        `/orgs/${orgId}/projects/${id}/domain-verification`,
      );
    },

    checkDomainVerification(orgId, id) {
      return request<CheckChallengeResult>(
        'POST',
        `/orgs/${orgId}/projects/${id}/domain-verification/check`,
      );
    },

    getDomainVerification(orgId, id) {
      return request<DomainVerificationStatusResult>(
        'GET',
        `/orgs/${orgId}/projects/${id}/domain-verification`,
      );
    },

    getRetention(orgId) {
      return request<RetentionSummary>('GET', `/orgs/${orgId}/retention`);
    },

    listScans(orgId, projectId) {
      return request<ScanSummary[]>('GET', `/orgs/${orgId}/projects/${projectId}/scans`);
    },

    getScanObservations(orgId, projectId, scanId, opts) {
      const params = new URLSearchParams();
      if (opts?.limit != null) params.set('limit', String(opts.limit));
      if (opts?.cursor != null) params.set('cursor', opts.cursor);
      const qs = params.toString();
      return request<ScanObservationsPage>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/scans/${scanId}/observations${qs ? `?${qs}` : ''}`,
      );
    },

    runScan(orgId, projectId, body) {
      return request<ScanSummary>(
        'POST',
        `/orgs/${orgId}/projects/${projectId}/scans`,
        body && body.scheduledAtMs !== undefined ? { scheduledAtMs: body.scheduledAtMs } : undefined,
      );
    },

    stopScan(orgId, projectId, scanId) {
      return request<ScanSummary>(
        'POST',
        `/orgs/${orgId}/projects/${projectId}/scans/${scanId}/stop`,
      );
    },

    journeyHealth(orgId, projectId) {
      return request<JourneyHealthResult>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/journey-health`,
      );
    },

    journeyEvidence(orgId, projectId, journeyType) {
      return request<JourneyEvidenceResponse>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/journeys/${encodeURIComponent(journeyType)}/evidence`,
      );
    },

    journeyDetectedSteps(orgId, projectId, journeyType) {
      return request<DetectedStepsResponse>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/journeys/${encodeURIComponent(journeyType)}/steps`,
      );
    },

    getJourneyModelRevisions(orgId, projectId, journeyId) {
      return request<ModelRevisionSummary[]>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/journeys/${encodeURIComponent(journeyId)}/model-revisions`,
      );
    },

    getJourneyModelRevision(orgId, projectId, journeyId, revisionId) {
      return request<MarkedRevisionView>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/journeys/${encodeURIComponent(journeyId)}/model-revisions/${encodeURIComponent(revisionId)}`,
      );
    },

    postJourneyModel(orgId, projectId, journeyId) {
      return request<JourneyModelingOutcome>(
        'POST',
        `/orgs/${orgId}/projects/${projectId}/journeys/${encodeURIComponent(journeyId)}/model-revisions`,
      );
    },

    getJourneyModelingStatus(orgId, projectId, journeyId) {
      return request<JourneyModelingStatusView>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/journeys/${encodeURIComponent(journeyId)}/modeling-status`,
      );
    },

    executionEvidence(orgId, projectId, journeyType, source) {
      const suffix = source ? `?source=${encodeURIComponent(source)}` : '';
      return request<ExecutionEvidenceResponse>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/journeys/${encodeURIComponent(journeyType)}/execution-evidence${suffix}`,
      );
    },

    listJourneys(orgId, projectId) {
      return request<JourneyListResponse>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/journeys`,
      );
    },

    getJourneyDetail(orgId, projectId, journeyId) {
      return request<JourneyDetailResponse>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/journeys/${encodeURIComponent(journeyId)}`,
      );
    },

    ingestJourneys(orgId, projectId, input) {
      return request<IngestJourneysResult>(
        'POST',
        `/orgs/${orgId}/projects/${projectId}/journeys`,
        input,
      );
    },

    triggerJourneyRun(orgId, projectId, input) {
      return request<TriggerRunResult>(
        'POST',
        `/orgs/${orgId}/projects/${projectId}/journeys/runs`,
        input,
      );
    },

    cancelJourneyRun(orgId, projectId, runId) {
      return request<CancelRunResult>(
        'POST',
        `/orgs/${orgId}/projects/${projectId}/journeys/runs/${encodeURIComponent(runId)}/cancel`,
      );
    },

    defectsList(orgId, projectId, params) {
      const qs = new URLSearchParams();
      if (params?.limit != null) qs.set('limit', String(params.limit));
      if (params?.offset != null) qs.set('offset', String(params.offset));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request<DefectsListResponse>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/defects${suffix}`,
      );
    },

    getDefect(orgId, projectId, defectId) {
      return request<DefectDetail>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/defects/${defectId}`,
      );
    },

    getDefectReviewHistory(orgId, projectId, defectId) {
      return request<DefectReviewHistoryResponse>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/defects/${defectId}/reviews`,
      );
    },

    reviewDefect(orgId, projectId, defectId, body) {
      return request<DefectReviewResult>(
        'POST',
        `/orgs/${orgId}/projects/${projectId}/defects/${defectId}/reviews`,
        body,
      );
    },

    generateTestCase(orgId, projectId, journeyId) {
      return request<TestCaseGenerationResult>(
        'POST',
        `/orgs/${orgId}/projects/${projectId}/journeys/${encodeURIComponent(journeyId)}/test-cases`,
      );
    },

    listTestCases(orgId, projectId) {
      return request<TestCaseView[]>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/test-cases`,
      );
    },

    exportTestCase(orgId, projectId, testCaseId, format) {
      return requestText(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/test-cases/${testCaseId}/export?format=${format}`,
      );
    },

    getTestCaseFormatPreference(orgId, projectId, testCaseId) {
      return request<{ format: ExportFormat }>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/test-cases/${testCaseId}/format-preference`,
      );
    },

    setTestCaseFormatPreference(orgId, projectId, testCaseId, format) {
      return request<{ format: ExportFormat }>(
        'PUT',
        `/orgs/${orgId}/projects/${projectId}/test-cases/${testCaseId}/format-preference`,
        { format },
      );
    },

    listTestCaseVersions(orgId, projectId, testCaseId) {
      return request<TestCaseView[]>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/test-cases/${testCaseId}/versions`,
      );
    },

    editTestCase(orgId, projectId, testCaseId, renderedText) {
      return request<TestCaseView>(
        'POST',
        `/orgs/${orgId}/projects/${projectId}/test-cases/${testCaseId}/revisions`,
        { renderedText },
      );
    },

    setTestCaseStatus(orgId, projectId, testCaseId, status) {
      return request<{ status: TestCaseStatus }>(
        'POST',
        `/orgs/${orgId}/projects/${projectId}/test-cases/${testCaseId}/status`,
        { status },
      );
    },

    listMembers(orgId) {
      return request<MemberSummary[]>('GET', `/orgs/${orgId}/members`);
    },

    addMember(orgId, input) {
      return request<MemberSummary>('POST', `/orgs/${orgId}/members`, input);
    },

    updateMemberRole(orgId, userId, role) {
      return request<MemberSummary>('PATCH', `/orgs/${orgId}/members/${userId}`, { role });
    },

    removeMember(orgId, userId) {
      return request<void>('DELETE', `/orgs/${orgId}/members/${userId}`);
    },

    updateRetention(orgId, input) {
      return request<RetentionSummary>('PATCH', `/orgs/${orgId}/retention`, input);
    },

    getLlmKey(orgId) {
      return request<LlmKeyStatus>('GET', `/orgs/${orgId}/llm-key`);
    },
    getLlmProviders(orgId) {
      return request<LlmProviderDescriptor[]>('GET', `/orgs/${orgId}/llm-key/providers`);
    },
    setLlmKey(orgId, input) {
      return request<LlmKeyStatus>('PUT', `/orgs/${orgId}/llm-key`, input);
    },
    listLlmKeyModels(orgId, input) {
      return request<LlmModelsResult>('POST', `/orgs/${orgId}/llm-key/models`, input);
    },
    revokeLlmKey(orgId) {
      return request<void>('DELETE', `/orgs/${orgId}/llm-key`);
    },
    getCapabilityQuote(orgId) {
      return request<CapabilityQuoteView>('GET', `/orgs/${orgId}/llm-capability/quote`);
    },
    recordCapabilityConsent(orgId) {
      return request<CapabilityConsentView>('POST', `/orgs/${orgId}/llm-capability/consent`);
    },
    enqueueCapabilityProbe(orgId) {
      return request<CapabilityRunView>('POST', `/orgs/${orgId}/llm-capability/probes`);
    },
    async getLatestCapabilityRun(orgId) {
      // The endpoint answers 200 with an empty body when no probe exists; `request`
      // surfaces that as undefined, so normalise to null to honour the return type.
      return (
        (await request<CapabilityRunView | null>(
          'GET',
          `/orgs/${orgId}/llm-capability/probes/latest`,
        )) ?? null
      );
    },
    async getCapabilityRun(orgId, runId) {
      return (
        (await request<CapabilityRunView | null>(
          'GET',
          `/orgs/${orgId}/llm-capability/probes/${runId}`,
        )) ?? null
      );
    },
    getIntegrations(orgId) {
      return request<IntegrationSummary[]>('GET', `/orgs/${orgId}/integrations`);
    },
    getTrackerSettings(orgId, projectId) {
      return request<TrackerSettingsView>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/tracker-settings`,
      );
    },
    updateTrackerSettings(orgId, projectId, input) {
      return request<TrackerSettingsView>(
        'PATCH',
        `/orgs/${orgId}/projects/${projectId}/tracker-settings`,
        input,
      );
    },
    getConnectedValidationSettings(orgId, projectId) {
      return request<ConnectedValidationSettingsView>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/connected-validation`,
      );
    },
    getTestCredentials(orgId, projectId) {
      return request<TestCredentialsView>(
        'GET',
        `/orgs/${orgId}/projects/${projectId}/connected-validation/credentials`,
      );
    },
    setTestCredentials(orgId, projectId, input) {
      return request<TestCredentialsView>(
        'PUT',
        `/orgs/${orgId}/projects/${projectId}/connected-validation/credentials`,
        input,
      );
    },
    deleteTestCredentials(orgId, projectId) {
      return request<void>(
        'DELETE',
        `/orgs/${orgId}/projects/${projectId}/connected-validation/credentials`,
      );
    },

    get<T>(path: string) {
      return request<T>('GET', path);
    },
    post<T>(path: string, body?: unknown) {
      return request<T>('POST', path, body);
    },
    patch<T>(path: string, body?: unknown) {
      return request<T>('PATCH', path, body);
    },
    del<T>(path: string) {
      return request<T>('DELETE', path);
    },

    _resetCsrf() {
      csrfToken = null;
      inFlightCsrf = null;
    },
  };
}

function statusToCode(status: number): ApiErrorCode {
  switch (status) {
    case 400:
      return 'bad_request';
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 422:
      return 'unprocessable_entity';
    case 429:
      return 'too_many_requests';
    default:
      return 'internal_error';
  }
}

function defaultMessage(status: number): string {
  switch (status) {
    case 401:
      return 'Unauthorized';
    case 403:
      return 'Forbidden';
    case 429:
      return 'Too many requests';
    case 400:
    case 422:
      return 'Invalid request';
    default:
      return 'An unexpected error occurred';
  }
}

function parseFields(input: unknown): FieldError[] | undefined {
  if (!Array.isArray(input)) return undefined;
  const fields: FieldError[] = [];
  for (const item of input) {
    if (
      item &&
      typeof item === 'object' &&
      typeof (item as FieldError).field === 'string' &&
      typeof (item as FieldError).message === 'string'
    ) {
      fields.push({ field: (item as FieldError).field, message: (item as FieldError).message });
    }
  }
  return fields.length > 0 ? fields : undefined;
}

/** App-wide default client instance (same-origin unless VITE_API_BASE_URL is set). */
export const apiClient = createApiClient({ baseUrl: import.meta.env.VITE_API_BASE_URL ?? '' });
